import { Component, Injector, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DataService } from '../../../core/service/base/data.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { PropertyHelpTextService } from '../../service/helpText/property-help-text.service';
import { FormBuilder, FormControl, FormGroup, FormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserAccountService } from '../../../common/service/user-account.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LanguageService } from '../../../core/service/language.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { LocalDate } from '../../../core/model/local-date';
import { PropertyTax } from '../../model/property-tax';
import { PropertyTaxService } from '../../service/data/property-tax-service';
import { TaxTypeCode } from '../../model/tax-type-code';
import { Building } from '../../model/building';
import { BuildingPropertyTaxAssessment } from '../../model/building-property-tax-assesment';
import { Money } from '../../../core/model/money';
import { PropertyFormCommandHandler } from '../../service/command/property-form-command-handler.service';

@Component({
    selector: 'app-property-tax-form',
    templateUrl: './property-tax-form.component.html',
    styleUrls: ['./property-tax-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        PropertyFormCommandHandler,
        PropertyHelpTextService,
        { provide: DataService, useExisting: PropertyTaxService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PropertyTaxComponent extends FormComponentBase<PropertyTax> {
  constructor(
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _dataService: PropertyTaxService,
    private _cdf: ColumnDefinitionFactory,
    private _actionWindowService: ActionWindowService,
    private _injector: Injector,
    private _nativeWindow: NativeWindowService,
    private _userAccountService: UserAccountService,
    private _languageService: LanguageService,
    private cdRef: ChangeDetectorRef,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: PropertyFormCommandHandler,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
  }

  override ngOnInit(): void {
    // Initialize immediately without waiting for parent route params
    this.init(this.createModel());
  }
  protected createModel(): PropertyTax {
    return new PropertyTax();
  }
  @Input() propertyName: string;
  @Input() propertyId: string;
  @Input() buildings: Building[];
  @Input() propertyTaxes: PropertyTax[];
  @Input() propertyTaxValues: PropertyTaxValue[];
  @Output() propertyTaxesChange = new EventEmitter<{
    drafts: PropertyTax[];
    removedPropertyTaxIds: string[];
    removedBuildingPropertyTaxAssessmentIds: string[];
    hasChanges: boolean;
  }>();

  yearSelection: SelectionItem[] = [];
  typeCodeSelection: SelectionItem[];
  showPropertyTaxEditor = false;
  assessmentYears: number[];
  selectedTaxAssessmentYear: number;
  // Cache per index to avoid rebuilding identical select lists every change detection
  private typeCodeSelectionCache: Map<number, { key: string; items: SelectionItem[] }> = new Map();
  private buildingAssessmentsByIndex: Map<number, BuildingPropertyTaxAssessment[]> = new Map();
  private removedPropertyTaxIds: string[] = [];
  private removedBuildingPropertyTaxAssessmentIds: string[] = [];
  private suppressDraftChangeTracking = false;
  get propertyTaxIds(): string[] {
    return (this.propertyTaxes || []).map(propertyTax => (propertyTax as any).id).filter(id => !!id);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['propertyTaxes'] && this.propertyTaxes) {
      // välj senaste år från inkommande skatter om inget valt
      const latest = Math.max(...(this.propertyTaxes || []).map(t => Number(t.assessmentYear) || 0));
      const yearCtrl = this.form?.get('selectedTaxAssessmentYear');
      if (yearCtrl && latest && yearCtrl.value !== latest) {
        yearCtrl.setValue(latest, { emitEvent: false });
        this.selectedTaxAssessmentYear = latest;
      }
      this.getPropertyTaxesByYear();
      this.updatePropertyTaxTotalsForYear(this.selectedTaxAssessmentYear || latest || Number(yearCtrl?.value));
      this.updatePropertyTotalsFromAssessments();
    }

    if (changes['propertyTaxValues'] && this.form) {
      const year = Number(this.form.get('selectedTaxAssessmentYear')?.value);
      this.updatePropertyTaxTotalsForYear(year);
      this.updatePropertyTotalsFromAssessments();
    }
  }

  protected getFormGroup(): FormGroup {
    const formGroup = this._fb.group({
      selectedTaxAssessmentYear: this._cf.create(
        'PROPERTY_TAX.ASSESSMENT_YEAR',
        this.dataContext && (this.dataContext as any).selectedTaxAssessmentYear
      ),
      decisionDate: this._cf.create('PROPERTY_TAX.DECISION_DATE', this.dataContext.decisionDate),
      assessmentUnitTypeCode: this._cf.create('PROPERTY_TAX.TYPE.PROPERTY_TAX_TYPE', this.dataContext.assessmentUnitTypeCode),
      propertyName: this._cf.createReadonly('PROPERTY.NAME', this.propertyName),
      typeCodeBuildingSum: this._cf.createReadonly(
        'PROPERTY_TAX.TOTAL_SUM_TYPE_CODE_BUILDING',
        this.dataContext?.typeCodeBuildingSum?.amount
      ),
      typeCodeLandSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_TYPE_CODE_LAND', this.dataContext?.typeCodeLandSum?.amount),
      typeCodeTotalSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_TYPE_CODE', this.dataContext?.typeCodeTotalSum?.amount),
      propertyTaxBuildingSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_PROPERTY_DWELLING_AND_PREMISES', null),
      propertyTaxLandSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_PROPERTY_LAND', null),
      propertyTaxTotalSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_PROPERTY', null),
      propertyTaxTaxablePremisesSum: this._cf.createReadonly('PROPERTY_TAX.TAXABLE_SUM_PREMISES', null),
      propertyTaxTaxableLandSum: this._cf.createReadonly('PROPERTY_TAX.TAXABLE_SUM_PREMISES_LAND', null),
      propertyTaxTaxableSum: this._cf.createReadonly('PROPERTY_TAX.TAXABLE_SUM', null),
      propertyTaxForms: this._fb.array([]),
    });
    return formGroup;
  }

  get propertyTaxForms(): FormArray {
    return this.form.get('propertyTaxForms') as FormArray;
  }

  private isAssessmentTypeCodeUsed(code: any, excludeIndex: number = -1): boolean {
    const normalizedCode = code != null ? String(code) : null;
    if (normalizedCode == null) return false;
    const propertyTaxControls = this.propertyTaxForms?.controls ?? [];
    for (let index = 0; index < propertyTaxControls.length; index++) {
      if (index === excludeIndex) continue;
      const otherCode = String((propertyTaxControls[index] as FormGroup).get('assessmentUnitTypeCode')?.value ?? '');
      if (otherCode && otherCode === normalizedCode) {
        return true;
      }
    }
    return false;
  }

  private attachAssessmentTypeUniqueness(group: FormGroup): void {
    const control = group.get('assessmentUnitTypeCode');
    if (!control) return;
    this.AddSubscription(
      control.valueChanges.subscribe(value => {
        if (value == null) return;
        const currentIndex = this.propertyTaxForms.controls.indexOf(group);
        if (this.isAssessmentTypeCodeUsed(value, currentIndex)) {
          this.messageService.showError('Skattetypen är redan vald för året. Välj en annan.');
          control.setValue(null, { emitEvent: false });
        }
      })
    );
  }

  get propertyTaxIndicesExisting(): number[] {
    const propertyTaxControls = this.propertyTaxForms?.controls ?? [];
    const existingIndices: number[] = [];
    for (let index = 0; index < propertyTaxControls.length; index++) {
      const propertyTaxId = (propertyTaxControls[index] as FormGroup).get('id')?.value;
      if (propertyTaxId) existingIndices.push(index);
    }
    return existingIndices;
  }

  get propertyTaxIndicesNew(): number[] {
    const propertyTaxControls = this.propertyTaxForms?.controls ?? [];
    const newIndices: number[] = [];
    for (let index = 0; index < propertyTaxControls.length; index++) {
      const propertyTaxId = (propertyTaxControls[index] as FormGroup).get('id')?.value;
      if (!propertyTaxId) newIndices.push(index);
    }
    return newIndices;
  }

  private createPropertyTaxFormGroupByYear(init?: any): FormGroup {
    const group = this._fb.group({
      id: this._cf.create('PROPERTY_TAX.ID', init?.id ?? null),
      assessmentUnitTypeCode: this._cf.create('PROPERTY_TAX.TYPE.PROPERTY_TAX_TYPE', init?.assessmentUnitTypeCode ?? null),
      decisionDate: this._cf.create('PROPERTY_TAX.DECISION_DATE', init?.decisionDate ?? null),
      typeCodeBuildingSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_TYPE_CODE_BUILDING', init?.typeCodeBuildingSum?.amount),
      typeCodeLandSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_TYPE_CODE_LAND', init?.typeCodeLandSum?.amount),
      typeCodeTotalSum: this._cf.createReadonly('PROPERTY_TAX.TOTAL_SUM_TYPE_CODE', init?.typeCodeTotalSum?.amount),
    });
    return group;
  }

  private toTaxTypeEntries(src: unknown): Array<[number, any]> {
    if (src instanceof Map) {
      return Array.from(src.entries());
    }
    if (Array.isArray(src)) {
      // If backend returns an array of TaxTypeCode, use propertyType as the numeric key
      return (src as any[])
        .filter(v => v && (typeof v.propertyType === 'number' || typeof v.PropertyType === 'number'))
        .map(v => [Number(v.propertyType ?? v.PropertyType), v]);
    }
    if (src && typeof src === 'object' && !Array.isArray(src)) {
      return Object.keys(src as any).map(k => [Number(k), (src as any)[k]]);
    }
    return [];
  }

  initPropertyTaxTypeSelection(propertyTaxTypeCodes: Map<number, TaxTypeCode>): SelectionItem[] {
    const entries = this.toTaxTypeEntries(propertyTaxTypeCodes);
    return entries
      .filter(([code, v]) => Number.isFinite(code) && v)
      .sort((a, b) => a[0] - b[0])
      .map(([code, info]) => {
        const name = info?.name ?? info?.Name ?? '';
        // Do not disable based on server flag; uniqueness enforced per-year in UI
        return new SelectionItem(`${code}, ${name}`, code, false);
      });
  }

  protected init(data: PropertyTax) {
    super.init(data);
    // Build the selectable assessment years first
    const startYear = 2000;
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = startYear; y <= currentYear; y++) {
      years.push(y);
    }
    this.assessmentYears = years;

    // Populate selection items from computed years
    this.yearSelection = (this.assessmentYears || []).map(y => new SelectionItem(y.toString(), y));

    const yearCtrl = this.form.controls['selectedTaxAssessmentYear'] as FormControl;
    if (yearCtrl) {
      const picked = this.pickLatestAssessmentYear(this.assessmentYears || []);
      if (picked != null && picked !== yearCtrl.value) {
        yearCtrl.setValue(Number(picked), { emitEvent: false });
      }
      this.selectedTaxAssessmentYear = Number(yearCtrl.value ?? picked);
      this.updatePropertyTaxTotalsForYear(this.selectedTaxAssessmentYear);
      this.updatePropertyTotalsFromAssessments();

      this.AddSubscription(
        yearCtrl.valueChanges.subscribe(val => {
          const num = Number(val);
          this.selectedTaxAssessmentYear = num;
          this.updatePropertyTaxTotalsForYear(num);
          this.getPropertyTaxesByYear();
          this.updatePropertyTotalsFromAssessments();
        })
      );
    }

    this.AddSubscription(
      this.form.controls['selectedTaxAssessmentYear'].valueChanges.subscribe(val => {
        if (this.dataContext) {
          this.selectedTaxAssessmentYear = val;
        }
      })
    );
    this.AddSubscription(
      this.form.controls['decisionDate'].valueChanges.subscribe(val => {
        if (this.dataContext) {
          this.dataContext.decisionDate = LocalDate.fromDate(val);
        }
      })
    );
    this.AddSubscription(
      this.form.controls['assessmentUnitTypeCode'].valueChanges.subscribe(val => {
        this.dataContext.assessmentUnitTypeCode = val;
      })
    );

    this.AddSubscription(
      this._dataService.getPropertyTaxTypeCodes().subscribe(res => {
        this.typeCodeSelection = this.initPropertyTaxTypeSelection(res);
        this.typeCodeSelectionCache.clear();
      })
    );

    this.AddSubscription(
      this.propertyTaxForms.valueChanges.subscribe(() => {
        if (!this.suppressDraftChangeTracking) {
          this.emitDrafts(true);
        }
      })
    );

    this.getPropertyTaxesByYear();
  }

  private getPropertyTaxesByYear(): void {
    if (!this.form) return;
    const selectedYear = Number(this.form.controls['selectedTaxAssessmentYear']?.value);
    if (!this.propertyId || !selectedYear) return;

    const taxesForYear = (this.propertyTaxes || []).filter(t => Number(t.assessmentYear) === selectedYear);

    this.removedPropertyTaxIds = [];
    this.removedBuildingPropertyTaxAssessmentIds = [];
    this.buildingAssessmentsByIndex.clear();
    this.suppressDraftChangeTracking = true;
    try {
      this.propertyTaxForms.clear();

      taxesForYear.forEach((propertyTax, idx) => {
        const formGroup = this.createPropertyTaxFormGroupByYear({
          id: propertyTax.id,
          assessmentUnitTypeCode: propertyTax.assessmentUnitTypeCode != null ? Number(propertyTax.assessmentUnitTypeCode) : null,
          decisionDate: LocalDate.fromDate(propertyTax?.decisionDate),
          typeCodeBuildingSum: propertyTax?.typeCodeBuildingSum,
          typeCodeLandSum: propertyTax?.typeCodeLandSum,
          typeCodeTotalSum: propertyTax?.typeCodeTotalSum,
        });
        this.propertyTaxForms.push(formGroup);
        this.attachAssessmentTypeUniqueness(formGroup);
        if (propertyTax?.buildingPropertyTaxAssessments) {
          const normalized = propertyTax.buildingPropertyTaxAssessments.map(a => ({
            ...a,
            isNew: !a?.id,
          }));
          this.buildingAssessmentsByIndex.set(idx, normalized as any);
        }
      });
    } finally {
      this.suppressDraftChangeTracking = false;
    }
    this.updateTypeCodeSumsForAll();
    this.updatePropertyTotalsFromAssessments();
    this.emitDrafts(false);
  }

  private pickLatestAssessmentYear(years: number[], todayYear: number = new Date().getFullYear()): number | null {
    if (!Array.isArray(years) || years.length === 0) return null;
    const sorted = [...years].sort((a, b) => a - b);
    let chosen = sorted[0];
    for (const y of sorted) {
      if (y <= todayYear) chosen = y;
      else break;
    }
    return chosen;
  }

  startAddAssessmentUnit(): void {
    const allCodesUsed =
      (this.typeCodeSelection || []).length > 0 &&
      this.propertyTaxIndicesExisting.length + this.propertyTaxIndicesNew.length >= (this.typeCodeSelection || []).length;
    if (allCodesUsed) {
      this.messageService.showError('Alla skattetypkoder är redan använda för valt år.');
      return;
    }
    const formGroup = this.createPropertyTaxFormGroupByYear();
    this.propertyTaxForms.push(formGroup);
    this.updateTypeCodeSumsForIndex(this.propertyTaxForms.length - 1);
    this.attachAssessmentTypeUniqueness(formGroup);
    this.showPropertyTaxEditor = true;
    this.typeCodeSelectionCache.clear();
    this.updatePropertyTotalsFromAssessments();
    this.emitDrafts(true);
  }

  removeAssessmentUnit(i: number): void {
    if (i >= 0 && i < this.propertyTaxForms.length) {
      const group = this.propertyTaxForms.at(i) as FormGroup;
      const idToRemove = group.get('id')?.value;
      if (idToRemove) {
        this.addUnique(this.removedPropertyTaxIds, idToRemove);
      }
      const buildingAssessments = this.buildingAssessmentsByIndex.get(i) || [];
      for (const assessment of buildingAssessments) {
        const assessmentId: any = (assessment as any)?.id;
        if (assessmentId) {
          this.addUnique(this.removedBuildingPropertyTaxAssessmentIds, String(assessmentId));
        }
      }
      this.propertyTaxForms.removeAt(i);
      this.buildingAssessmentsByIndex.delete(i);
      this.reindexBuildingAssessmentMap();
      this.updateTypeCodeSumsForAll();
      this.typeCodeSelectionCache.clear();
      this.updatePropertyTotalsFromAssessments();
      this.emitDrafts(true);
    }
    if (this.propertyTaxForms.length === 0) {
      this.showPropertyTaxEditor = false;
    }
  }

  getTypeCodeName(code: any): string {
    const item = (this.typeCodeSelection || []).find(it => String(it.value) === String(code));
    return item?.name ?? String(code ?? '');
  }

  // Per-accordion select list that disables codes chosen in other property tax forms
  getTypeCodeSelectionForIndex(index: number): SelectionItem[] {
    if (!this.form) return this.typeCodeSelection || [];
    const array = this.form.get('propertyTaxForms') as FormArray;
    if (!array) return this.typeCodeSelection || [];

    const usedCodes = new Set(
      array.controls
        .map((ctrl, idx) => (idx === index ? null : (ctrl as FormGroup).get('assessmentUnitTypeCode')?.value))
        .filter(v => v !== null && v !== undefined)
        .map(v => String(v))
    );

    const cacheKey = Array.from(usedCodes).sort().join('|') + '::' + (this.typeCodeSelection?.length ?? 0);
    const cached = this.typeCodeSelectionCache.get(index);
    if (cached && cached.key === cacheKey) {
      return cached.items;
    }

    const items = (this.typeCodeSelection || []).map(item => {
      const isUsedElsewhere = usedCodes.has(String(item.value));
      return new SelectionItem(item.name, item.value, isUsedElsewhere ? true : item.disabled);
    });

    this.typeCodeSelectionCache.set(index, { key: cacheKey, items });
    return items;
  }

  getPropertyTaxHeaderByIndex(index: number): string {
    const grp = this.propertyTaxForms.at(index) as FormGroup;
    const code = grp.get('assessmentUnitTypeCode')?.value;
    const name = this.getTypeCodeName(code);
    return name
      ? name
        : this._languageService.instant('PROPERTY_TAX.NEW_PROPERTY_TAX_HEADER', {
          index: index + 1,
        });
  }

  getBuildingAssessments(index: number): BuildingPropertyTaxAssessment[] {
    return this.buildingAssessmentsByIndex.get(index) || [];
  }

  // Building ids already used by other property tax forms (to disable in child selector)
  getDisabledBuildingIds(index: number): string[] {
    const disabled = new Set<string>();
    for (const [idx, assessments] of this.buildingAssessmentsByIndex.entries()) {
      if (idx === index) continue;
      for (const assessment of assessments || []) {
        const buildingId = (assessment as any)?.buildingId ?? (assessment as any)?.building?.id ?? (assessment as any)?.buildingNumber;
        if (buildingId != null) {
          disabled.add(String(buildingId));
        }
      }
    }
    return Array.from(disabled);
  }

  onAssessmentsChange(
    index: number,
    payload: { assessments: BuildingPropertyTaxAssessment[]; removedAssessmentIds: string[]; hasChanges: boolean }
  ): void {
    const normalized = (payload?.assessments || []).map(a => ({
      ...a,
      isNew: !(a as any)?.id,
    })) as any;
    this.buildingAssessmentsByIndex.set(index, normalized);
    (payload?.removedAssessmentIds || []).forEach(id => this.addUnique(this.removedBuildingPropertyTaxAssessmentIds, String(id)));
    this.emitDrafts(payload?.hasChanges ?? false);
    this.updateTypeCodeSumsForIndex(index);
    this.updatePropertyTotalsFromAssessments();
    this.cdRef.markForCheck();
  }

  private buildDrafts(): Array<{
    id?: string | null;
    propertyId?: string;
    assessmentUnitTypeCode: any;
    assessmentYear: number;
    decisionDate: LocalDate | null;
    buildingPropertyTaxAssessments: BuildingPropertyTaxAssessment[];
  }> {
    const selectedYear = Number(this.form.controls['selectedTaxAssessmentYear'].value);
    const drafts: Array<{
      id?: string | null;
      propertyId?: string;
      assessmentUnitTypeCode: any;
      assessmentYear: number;
      decisionDate: LocalDate | null;
      buildingPropertyTaxAssessments: BuildingPropertyTaxAssessment[];
      isNew?: boolean;
    }> = [];
    const propertyTaxFormControls = this.propertyTaxForms?.controls ?? [];
    for (let index = 0; index < propertyTaxFormControls.length; index++) {
      const propertyTaxFormGroup = propertyTaxFormControls[index] as FormGroup;
      const decisionDateValue = propertyTaxFormGroup.get('decisionDate')?.value;
      const childAssessments = (this.buildingAssessmentsByIndex.get(index) || []).map(assessment => ({
        ...assessment,
        isNew: !(assessment as any)?.id,
      })) as any;
      drafts.push({
        id: propertyTaxFormGroup.get('id')?.value,
        propertyId: this.propertyId,
        assessmentUnitTypeCode: propertyTaxFormGroup.get('assessmentUnitTypeCode')?.value,
        assessmentYear: selectedYear,
        decisionDate: decisionDateValue ? LocalDate.fromDate(decisionDateValue) : null,
        buildingPropertyTaxAssessments: childAssessments,
        isNew: !propertyTaxFormGroup.get('id')?.value,
      });
    }
    return drafts;
  }

  private emitDrafts(hasChanges: boolean): void {
    if (hasChanges && this.form) {
      this.form.markAsDirty({ onlySelf: true });
    }
    this.propertyTaxesChange.emit({
      // Drafts are structurally-partial PropertyTax objects assembled from the form; consumed/serialized as PropertyTax.
      drafts: this.buildDrafts() as unknown as PropertyTax[],
      removedPropertyTaxIds: [...this.removedPropertyTaxIds],
      removedBuildingPropertyTaxAssessmentIds: [...this.removedBuildingPropertyTaxAssessmentIds],
      hasChanges,
    });
  }

  private reindexBuildingAssessmentMap(): void {
    const propertyTaxFormControls = this.propertyTaxForms?.controls ?? [];
    const orderedAssessments = Array.from(this.buildingAssessmentsByIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, value]) => value || []);

    const newMap = new Map<number, BuildingPropertyTaxAssessment[]>();
    for (let index = 0; index < propertyTaxFormControls.length && index < orderedAssessments.length; index++) {
      newMap.set(index, orderedAssessments[index]);
    }
    this.buildingAssessmentsByIndex = newMap;
  }

  private addUnique(target: string[], id: string): void {
    if (id == null) {
      return;
    }
    const idStr = String(id);
    if (target.indexOf(idStr) === -1) {
      target.push(idStr);
    }
  }

  private getPropertyTaxValueForYear(year: number): PropertyTaxValue | null {
    if (!Number.isFinite(Number(year))) return null;
    return (this.propertyTaxValues || []).find(v => Number(v?.assessmentYear) === Number(year)) || null;
  }

  private updatePropertyTaxTotalsForYear(year: number): void {
    const match = this.getPropertyTaxValueForYear(year);
    this.form?.get('propertyTaxBuildingSum')?.setValue(match?.propertyTaxBuildingsSum?.amount ?? null, { emitEvent: false });
    this.form?.get('propertyTaxLandSum')?.setValue(match?.propertyTaxLandSum?.amount ?? null, { emitEvent: false });
    this.form?.get('propertyTaxTotalSum')?.setValue(match?.propertyTaxTotalSum?.amount ?? null, { emitEvent: false });
    this.form?.get('propertyTaxTaxablePremisesSum')?.setValue(match?.propertyTaxTaxablePremisesSum?.amount ?? null, { emitEvent: false });
    this.form?.get('propertyTaxTaxableLandSum')?.setValue(match?.propertyTaxTaxableLandSum?.amount ?? null, { emitEvent: false });
    this.form?.get('propertyTaxTaxableSum')?.setValue(match?.propertyTaxTaxableSum?.amount ?? null, { emitEvent: false });
  }

  private getAllAssessments(): BuildingPropertyTaxAssessment[] {
    return Array.from(this.buildingAssessmentsByIndex.values()).reduce<BuildingPropertyTaxAssessment[]>((acc, list) => {
      if (Array.isArray(list)) {
        acc.push(...list);
      }
      return acc;
    }, []);
  }

  private calculateTaxablePremisesSumForAssessments(assessments: BuildingPropertyTaxAssessment[]): number {
    if (!assessments || assessments.length === 0) return 0;
    return assessments.reduce((sum, a) => {
      const premises = (a as any)?.premisesTaxableValue;
      const value = typeof premises === 'number' ? premises : Number((premises as any)?.amount ?? 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

  private calculateTaxableLandSumForAssessments(assessments: BuildingPropertyTaxAssessment[]): number {
    if (!assessments || assessments.length === 0) return 0;
    return assessments.reduce((sum, a) => {
      const land = (a as any)?.premisesLandTaxableValue;
      const value = typeof land === 'number' ? land : Number((land as any)?.amount ?? 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

  private updatePropertyTotalsFromAssessments(): void {
    const allAssessments = this.getAllAssessments();
    if (!allAssessments || allAssessments.length === 0) {
      this.form?.get('propertyTaxBuildingSum')?.setValue(0, { emitEvent: false });
      this.form?.get('propertyTaxLandSum')?.setValue(0, { emitEvent: false });
      this.form?.get('propertyTaxTotalSum')?.setValue(0, { emitEvent: false });
      this.form?.get('propertyTaxTaxablePremisesSum')?.setValue(0, { emitEvent: false });
      this.form?.get('propertyTaxTaxableLandSum')?.setValue(0, { emitEvent: false });
      this.form?.get('propertyTaxTaxableSum')?.setValue(0, { emitEvent: false });
      return;
    }

    const buildingSum = this.calculateBuildingSumForAssessments(allAssessments);
    const landSum = this.calculateLandSumForAssessments(allAssessments);
    const taxablePremisesSum = this.calculateTaxablePremisesSumForAssessments(allAssessments);
    const taxableLandSum = this.calculateTaxableLandSumForAssessments(allAssessments);

    this.form?.get('propertyTaxBuildingSum')?.setValue(buildingSum, { emitEvent: false });
    this.form?.get('propertyTaxLandSum')?.setValue(landSum, { emitEvent: false });
    this.form?.get('propertyTaxTotalSum')?.setValue(buildingSum + landSum, { emitEvent: false });
    this.form?.get('propertyTaxTaxablePremisesSum')?.setValue(taxablePremisesSum, { emitEvent: false });
    this.form?.get('propertyTaxTaxableLandSum')?.setValue(taxableLandSum, { emitEvent: false });
    this.form?.get('propertyTaxTaxableSum')?.setValue(taxablePremisesSum + taxableLandSum, { emitEvent: false });
  }

  private calculateLandSumForAssessments(assessments: BuildingPropertyTaxAssessment[]): number {
    if (!assessments || assessments.length === 0) return 0;
    return assessments.reduce((sum, a) => {
      const premisesLand = Number((a as any)?.premisesLandTaxableValue?.amount ?? 0);
      const dwellingsLand = Number((a as any)?.dwellingsLandTaxableValue?.amount ?? 0);
      const safePremises = Number.isFinite(premisesLand) ? premisesLand : 0;
      const safeDwellings = Number.isFinite(dwellingsLand) ? dwellingsLand : 0;
      return sum + safePremises + safeDwellings;
    }, 0);
  }

  private calculateBuildingSumForAssessments(assessments: BuildingPropertyTaxAssessment[]): number {
    if (!assessments || assessments.length === 0) return 0;
    return assessments.reduce((sum, a) => {
      const premises = Number((a as any)?.premisesTaxableValue?.amount ?? 0);
      const dwellings = Number((a as any)?.dwellingsTaxableValue?.amount ?? 0);
      const safePremises = Number.isFinite(premises) ? premises : 0;
      const safeDwellings = Number.isFinite(dwellings) ? dwellings : 0;
      return sum + safePremises + safeDwellings;
    }, 0);
  }

  private updateTypeCodeSumsForIndex(index: number): void {
    const formGroup = this.propertyTaxForms?.at(index) as FormGroup;
    if (!formGroup) return;

    const assessments = this.buildingAssessmentsByIndex.get(index) || [];
    const landSum = this.calculateLandSumForAssessments(assessments);
    const buildingSum = this.calculateBuildingSumForAssessments(assessments);

    formGroup.get('typeCodeBuildingSum')?.setValue(buildingSum, { emitEvent: false });
    formGroup.get('typeCodeLandSum')?.setValue(landSum, { emitEvent: false });
    formGroup.get('typeCodeTotalSum')?.setValue(buildingSum + landSum, { emitEvent: false });
  }

  private updateTypeCodeSumsForAll(): void {
    const propertyTaxFormControls = this.propertyTaxForms?.controls ?? [];
    for (let i = 0; i < propertyTaxFormControls.length; i++) {
      this.updateTypeCodeSumsForIndex(i);
    }
  }
}

export class PropertyTaxValue {
  assessmentYear: number;
  propertyTaxBuildingsSum: Money;
  propertyTaxLandSum: Money;
  propertyTaxTotalSum: Money;
  propertyTaxTaxablePremisesSum: Money;
  propertyTaxTaxableLandSum: Money;
  propertyTaxTaxableSum: Money;
}
